import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, User, Briefcase, Award, Shield, Image, Wrench, MapPin, CheckCircle, Clock, AlertTriangle, ShieldCheck, Users } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useGlobalToast } from '@/contexts/ToastContext';
import { fetchCategories, Category } from '@/services/categoryService';
import { fetchCounties, County } from '@/services/locationService';

interface ProfileData {
  status: 'draft' | 'pending_verification' | 'verified' | 'rejected';
  verificationSubmittedAt?: string;
  verifiedAt?: string;
  rejectionReason?: string;
  profile: any;
  completedSteps?: number[];
  currentCompanyId?: string;
  currentCompanyName?: string;
}

export function IndividualProviderProfilePage() {
  const navigate = useNavigate();
  const { error, success } = useGlobalToast();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [counties, setCounties] = useState<County[]>([]);

  useEffect(() => {
    loadProfile();
    loadCategoriesAndCounties();
  }, []);

  const loadCategoriesAndCounties = async () => {
    try {
      const [fetchedCategories, fetchedCounties] = await Promise.all([
        fetchCategories(),
        fetchCounties()
      ]);
      setCategories(fetchedCategories);
      setCounties(fetchedCounties);
    } catch (err) {
      console.error('Failed to load categories/counties:', err);
    }
  };

  const loadProfile = async () => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://npm.rapidfix.ie';
      const apiUrl = `${apiBaseUrl}/api/v1/auth/providers/profile`;
      
      const response = await fetch(apiUrl, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        setProfileData({
          status: data.status || 'draft',
          verificationSubmittedAt: data.verification_submitted_at,
          verifiedAt: data.verified_at,
          rejectionReason: data.rejection_reason,
          profile: data.profile_data || {},
          completedSteps: data.completed_steps || [],
          currentCompanyId: data.current_company_id,
          currentCompanyName: data.current_company_name
        });
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
      error('Load Failed', 'Could not load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitForVerification = async () => {
    if (!profileData) {
      error('Error', 'Profile data not loaded');
      return;
    }
    
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://npm.rapidfix.ie';
      // Use correct endpoint: /profile/submit (not /profile/submit-verification)
      // The handler expects profile_data and completed_steps
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/providers/profile/submit`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          profile_data: profileData.profile || {},
          completed_steps: profileData.completedSteps || [1, 2, 5] // Use stored completed steps or default to required steps
        })
      });

      if (response.ok) {
        success('Submitted!', 'Your profile has been submitted for verification');
        loadProfile(); // Reload to show verification banner
    } else {
        const data = await response.json();
        error('Submission Failed', data.error || 'Could not submit profile for verification');
      }
    } catch (err) {
      error('Submission Failed', 'An error occurred while submitting');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Profile Not Found</h2>
          <p className="text-gray-600 mb-6">Please complete your profile to continue</p>
          <Button onClick={() => navigate('/profile/wizard/individual')}>
            Complete Profile
          </Button>
        </Card>
      </div>
    );
  }

  const isVerified = profileData.status === 'verified';
  const isPending = profileData.status === 'pending_verification';
  const isRejected = profileData.status === 'rejected';
  const canSubmitForVerification = !isVerified && !isPending;
  const employmentHistory = Array.isArray(profileData.profile?.employmentHistory) ? profileData.profile.employmentHistory : [];
  const currentCompanyId = profileData.profile?.parentCompanyId || profileData.currentCompanyId;
  const currentCompanyName = profileData.currentCompanyName || 'Company';

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 pb-20">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-6xl mx-auto px-4">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Profile' }]} />
        
        {/* Verification Status Banner - Only show if pending or rejected */}
        {isPending && (
          <Card className="mb-6 border-l-4 border-l-amber-500 mt-4">
            <div className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-gray-900">Verification In Progress</h3>
                  <Badge variant="warning" size="sm">Pending</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Your profile is under review. This typically takes 24-48 hours.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Submitted: {profileData.verificationSubmittedAt ? new Date(profileData.verificationSubmittedAt).toLocaleString() : 'Recently'}
                </p>
                </div>
              </div>
            </Card>
        )}

        {isRejected && (
          <Card className="mb-6 border-l-4 border-l-red-500">
            <div className="p-4">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-900">Verification Declined</h3>
                    <Badge variant="error" size="sm">Action Required</Badge>
                    </div>
                  <p className="text-sm text-gray-600">
                    Please review the feedback below and update your profile.
                  </p>
                    </div>
                    </div>
              {profileData.rejectionReason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 text-sm mb-2">Admin Feedback:</h4>
                  <p className="text-sm text-red-800 whitespace-pre-wrap">{profileData.rejectionReason}</p>
                  </div>
              )}
                  </div>
                </Card>
            )}

        {/* Profile Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
              {isVerified && (
                <Badge variant="success" className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Verified
                          </Badge>
                      )}
                    </div>
            <p className="text-gray-600">Manage your professional profile and settings</p>
                  </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/profile/wizard/individual')}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
            {canSubmitForVerification && (
              <Button variant="primary" onClick={handleSubmitForVerification}>
                <ShieldCheck className="w-4 h-4 mr-2" />
                Get Verified
              </Button>
                          )}
                        </div>
                  </div>

        {/* Verification Info Card */}
        {!isVerified && !isPending && (
          <Card className="mb-6 bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
            <div className="p-5 flex items-start gap-4">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-6 h-6 text-primary-600" />
                    </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-2">Get Verified & Stand Out</h3>
                <p className="text-sm text-gray-700 mb-3">
                  Verified providers get a trust badge on their quotes, increasing acceptance rates by up to 40%. 
                  Complete your profile and submit for verification to unlock this benefit.
                </p>
                <ul className="text-sm text-gray-700 space-y-1 mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary-600" />
                    Verified badge on all quotes
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary-600" />
                    Higher customer trust
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary-600" />
                    Priority in search results
                  </li>
                </ul>
                <Button size="sm" onClick={handleSubmitForVerification}>
                  Submit for Verification
                </Button>
                    </div>
                  </div>
                </Card>
        )}

        {/* Profile Details Grid */}
        {(currentCompanyId || employmentHistory.length > 0) && (
          <div className="mb-6 space-y-4">
            {currentCompanyId && (
              <Card className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-primary-600" />
                  <h3 className="font-bold text-gray-900">Company Association</h3>
                      </div>
                <div className="text-sm text-gray-700 space-y-1">
                  <div><span className="font-medium">Company:</span> {currentCompanyName}</div>
                  <div><span className="font-medium">Role:</span> {profileData.profile?.companyRole || 'Member'}</div>
                  <div><span className="font-medium">Employment:</span> {profileData.profile?.employmentType || 'N/A'}</div>
                  <div><span className="font-medium">Personal Jobs:</span> {profileData.profile?.canTakePersonalJobs ? 'Allowed' : 'Not Allowed'}</div>
                    </div>
                  </Card>
                )}

            {employmentHistory.length > 0 && (
              <Card className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-primary-600" />
                  <h3 className="font-bold text-gray-900">Employment History</h3>
                    </div>
                <div className="space-y-3 text-sm text-gray-700">
                  {employmentHistory.map((item: any, index: number) => (
                    <div key={`${item.companyId}-${index}`} className="border-b border-gray-200/60 pb-2 last:border-b-0 last:pb-0">
                      <div className="font-medium">{item.companyName || 'Company'}</div>
                      <div>Role: {item.role || 'Member'}</div>
                      <div>{formatDate(item.startDate)} to {item.endDate ? formatDate(item.endDate) : 'Present'}</div>
                    </div>
                  ))}
                  </div>
                </Card>
            )}
                      </div>
        )}
        <ProfileDetailsGrid 
          profile={profileData.profile} 
          navigate={navigate} 
          categories={categories}
          counties={counties}
        />
      </main>
    </div>
  );
}

// Reusable Profile Details Grid Component
function ProfileDetailsGrid({ profile, navigate, categories, counties }: { 
  profile: any; 
  navigate: any;
  categories: Category[];
  counties: County[];
}) {
  // Get category names from IDs
  const getSpecializationNames = () => {
    if (!profile.specializations || !Array.isArray(profile.specializations)) return [];
    return profile.specializations
      .map((id: string) => categories.find(c => c.id === id))
      .filter(Boolean)
      .map((cat: Category) => cat.name);
  };

  // Get county names from IDs
  const getServiceAreaNames = () => {
    if (!profile.serviceCounties || !Array.isArray(profile.serviceCounties)) return [];
    return profile.serviceCounties
      .map((id: string) => counties.find(c => c.id === id))
      .filter(Boolean)
      .map((county: County) => county.name);
  };

  const specializationNames = getSpecializationNames();
  const serviceAreaNames = getServiceAreaNames();

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Personal Information */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <User className="w-4 h-4 text-primary-600" />
            Personal Information
          </h3>
          <Button size="sm" variant="ghost" onClick={() => navigate('/profile/wizard/individual?step=1')}>
            <Edit className="w-3 h-3" />
                            </Button>
                          </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Name</span>
            <span className="text-sm font-medium">
              {profile.firstName && profile.lastName 
                ? `${profile.firstName} ${profile.lastName}`
                : <span className="text-gray-400 italic">Not provided</span>
              }
            </span>
                        </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Email</span>
            <span className="text-sm font-medium">
              {profile.email || <span className="text-gray-400 italic">Not provided</span>}
            </span>
                      </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Phone</span>
            <span className="text-sm font-medium">
              {profile.phone || <span className="text-gray-400 italic">Not provided</span>}
            </span>
                    </div>
                  </div>
                </Card>

      {/* Business Details */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-primary-600" />
            Business Details
          </h3>
          <Button size="sm" variant="ghost" onClick={() => navigate('/profile/wizard/individual?step=2')}>
            <Edit className="w-3 h-3" />
                    </Button>
                  </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Business Name</span>
            <span className="text-sm font-medium">
              {profile.businessName || <span className="text-gray-400 italic">Not provided</span>}
            </span>
                        </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Type</span>
            <span className="text-sm font-medium capitalize">
              {profile.providerType || <span className="text-gray-400 italic">Not provided</span>}
            </span>
                        </div>
          {profile.businessAddress && (
                        <div>
              <span className="text-sm text-gray-600">Address</span>
              <p className="text-sm font-medium mt-1">{profile.businessAddress}</p>
                    </div>
                  )}
          {profile.bio && (
                          <div>
              <span className="text-sm text-gray-600">Bio</span>
              <p className="text-sm font-medium mt-1 line-clamp-2">{profile.bio}</p>
                          </div>
                    )}
                  </div>
                </Card>

      {/* Qualifications */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Award className="w-4 h-4 text-primary-600" />
            Qualifications
          </h3>
          <Button size="sm" variant="ghost" onClick={() => navigate('/profile/wizard/individual?step=3')}>
            <Edit className="w-3 h-3" />
          </Button>
                      </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Experience</span>
            <span className="text-sm font-medium">
              {profile.yearsExperience 
                ? `${profile.yearsExperience} years`
                : <span className="text-gray-400 italic">Not provided</span>
              }
            </span>
                    </div>
          {profile.certifications && profile.certifications.length > 0 ? (
            <div>
              <span className="text-sm text-gray-600 block mb-2">Certifications</span>
              <div className="flex flex-wrap gap-1">
                {profile.certifications.map((cert: string, idx: number) => (
                  <Badge key={idx} variant="secondary" size="sm">{cert}</Badge>
                ))}
                  </div>
                      </div>
          ) : (
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Certifications</span>
              <span className="text-sm text-gray-400 italic">None added</span>
                      </div>
          )}
                  </div>
                </Card>

      {/* Insurance & Compliance */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary-600" />
            Insurance & Compliance
          </h3>
          <Button size="sm" variant="ghost" onClick={() => navigate('/profile/wizard/individual?step=3')}>
            <Edit className="w-3 h-3" />
          </Button>
                    </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Insurance</span>
            <span className="text-sm font-medium">
              {profile.insuranceProvider || <span className="text-gray-400 italic">Not provided</span>}
            </span>
                      </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Tax Number</span>
            <span className="text-sm font-medium">
              {profile.taxNumber || <span className="text-gray-400 italic">Not provided</span>}
            </span>
                    </div>
                  </div>
                </Card>

      {/* Portfolio - Full Width */}
      <Card className="p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Image className="w-4 h-4 text-primary-600" />
            Portfolio {profile.portfolioImages && profile.portfolioImages.length > 0 && `(${profile.portfolioImages.length} images)`}
          </h3>
          <Button size="sm" variant="ghost" onClick={() => navigate('/profile/wizard/individual?step=4')}>
            <Edit className="w-3 h-3" />
          </Button>
                    </div>
        {profile.portfolioImages && profile.portfolioImages.length > 0 ? (
          <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
            {profile.portfolioImages.map((image: string, idx: number) => (
              <img
                key={idx}
                src={image}
                alt={`Portfolio ${idx + 1}`}
                className="w-full h-24 object-cover rounded-lg border border-gray-200"
              />
            ))}
                      </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Image className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No portfolio images added yet</p>
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => navigate('/profile/wizard/individual?step=4')}>
              Add Images
            </Button>
                    </div>
        )}
                </Card>

      {/* Services & Pricing - Full Width */}
      <Card className="p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-primary-600" />
            Service Categories {specializationNames.length > 0 && `(${specializationNames.length})`}
          </h3>
          <Button size="sm" variant="ghost" onClick={() => navigate('/profile/wizard/individual?step=5')}>
            <Edit className="w-3 h-3" />
          </Button>
                      </div>
        {specializationNames.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
            {specializationNames.map((name: string, idx: number) => (
              <Badge key={idx} variant="secondary" size="sm" className="px-3 py-1.5">
                {name}
                          </Badge>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Wrench className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No service categories selected yet</p>
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => navigate('/profile/wizard/individual?step=5')}>
              Select Categories
            </Button>
                    </div>
        )}
        
        {/* Pricing Info */}
        {(profile.calloutFee || profile.minimumJobValue || profile.travelDistance) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Pricing Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {profile.calloutFee && (
                <div className="text-center p-3 bg-primary-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Callout Fee</p>
                  <p className="text-sm font-bold text-gray-900">€{profile.calloutFee}</p>
                      </div>
                    )}
              {profile.minimumJobValue && (
                <div className="text-center p-3 bg-primary-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Min Job Value</p>
                  <p className="text-sm font-bold text-gray-900">€{profile.minimumJobValue}</p>
                          </div>
                        )}
              {profile.travelDistance && (
                <div className="text-center p-3 bg-primary-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Travel Distance</p>
                  <p className="text-sm font-bold text-gray-900">{profile.travelDistance} km</p>
              </div>
            )}
              {profile.travelFee && (
                <div className="text-center p-3 bg-primary-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Travel Fee</p>
                  <p className="text-sm font-bold text-gray-900">€{profile.travelFee}</p>
              </div>
            )}
                      </div>
                    </div>
        )}
                </Card>

      {/* Service Areas - Full Width */}
      <Card className="p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary-600" />
            Service Areas (Counties) {serviceAreaNames.length > 0 && `(${serviceAreaNames.length})`}
                  </h3>
          <Button size="sm" variant="ghost" onClick={() => navigate('/profile/wizard/individual?step=5')}>
            <Edit className="w-3 h-3" />
                    </Button>
        </div>
        {serviceAreaNames.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {serviceAreaNames.map((area: string, idx: number) => (
              <Badge key={idx} variant="neutral" size="sm">{area}</Badge>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No service areas selected yet</p>
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => navigate('/profile/wizard/individual?step=5')}>
              Select Counties
                        </Button>
                    </div>
        )}
        
        {/* Availability Info */}
        {(profile.emergencyService || profile.weekendService) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Availability</h4>
            <div className="flex flex-wrap gap-2">
              {profile.emergencyService && (
                <Badge variant="success" size="sm" className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Emergency Services
                </Badge>
              )}
              {profile.weekendService && (
                <Badge variant="secondary" size="sm" className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Weekend Services
                </Badge>
                  )}
                </div>
              </div>
            )}
      </Card>
    </div>
  );
}
